import React from 'react';
import styled from '@grebban/style-system-react';
import { generateSrcSet } from '@fogia/core-web/libs/image';
import Text from 'components/Text';
import { above, media } from '@fogia/core-web/utils/mediaqueries';
import Link from '@fogia/core-web/components/Link';

const PRODUCT_NAME_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/13_100_510_-1',
    null,
    null,
    null,
    null,
    'Primary/15_120_510_-1',
];
const PRODUCT_DESIGNER_TYPOGRAPHY: (string | null)[] = [
    'Secondary/Mobile/13_120_400',
    null,
    null,
    null,
    null,
    'Secondary/15_120_400',
];

const Row = styled(Link)`
    width: 100%;
    display: grid;
    gap: var(--grid-gap);
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 0;
    border-top: 0.5px solid var(--stroke-default);
    cursor: pointer;
    grid-template-columns: repeat(12, 1fr);

    ${above['tablet.lg']} {
        grid-template-columns: repeat(10, 1fr);
    }

    ${media.hover} {
        &:hover {
            opacity: 0.7;
        }
    }
`;

const Info = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 2px;
    grid-column: 1 / 8;
    align-items: flex-start;

    ${above['tablet.lg']} {
        grid-column: 1 / 8;
    }
`;

const Image = styled('div')<{ $isWide: boolean }>`
    grid-column: ${({ $isWide }) => ($isWide ? '8 / 13' : '10 / 13')};

    ${above['tablet.lg']} {
        grid-column: ${({ $isWide }) => ($isWide ? '8 / 11' : '9 / 11')};
    }
`;

const ImageWrapper = styled('div')<{ $isWide: boolean }>`
    width: 100%;
    aspect-ratio: ${({ $isWide }) => ($isWide ? '16 / 10' : '4 / 5')};
    background-color: var(--ui-surface);
    border-radius: 2px;
    flex-shrink: 0;
    overflow: hidden;
`;

const Thumbnail = styled('img')`
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
`;

const SRCSET_WIDTHS = [200, 300, 400, 600, 800];
const SIZES_WIDE = '(min-width: 1280px) 13vw, 42vw';
const SIZES_NARROW = '(min-width: 1280px) 9vw, 25vw';

export interface SearchResultProductRowProps {
    name: string;
    designer: string | null;
    thumbnailUrl: string | null;
    isWide: boolean;
    onClick: () => void;
    url: string;
}

const SearchResultProductRow = ({
    name,
    designer,
    thumbnailUrl,
    isWide,
    url,
    onClick,
}: SearchResultProductRowProps) => (
    <Row to={url} onClick={onClick}>
        <Info>
            <Text $as="span" typography={PRODUCT_NAME_TYPOGRAPHY} color="var(--ui-primary)">
                {name}
            </Text>
            {designer && (
                <Text $as="span" typography={PRODUCT_DESIGNER_TYPOGRAPHY} color="var(--ui-primary)">
                    {designer}
                </Text>
            )}
        </Info>
        <Image $isWide={isWide}>
            <ImageWrapper $isWide={isWide}>
                {thumbnailUrl && (
                    <Thumbnail
                        src={thumbnailUrl}
                        srcSet={generateSrcSet(thumbnailUrl, SRCSET_WIDTHS)}
                        sizes={isWide ? SIZES_WIDE : SIZES_NARROW}
                        alt={name || ''}
                        loading="lazy"
                    />
                )}
            </ImageWrapper>
        </Image>
    </Row>
);

export default SearchResultProductRow;
